'use client'

import React from 'react'

import { useHubSpotForm } from '@/blocks/HubspotForm/hooks/useHubSpotForm'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { cn } from '@/utilities/ui'

import type {
  Hubspotform,
  Media,
  NewsletterFormBlock as NewsletterFormBlockProps,
} from '@/payload-types'

import formStyles from '@/blocks/HubspotForm/styles.module.css'
import styles from './styles.module.css'

type Props = NewsletterFormBlockProps & {
  disableInnerContainer?: boolean
}

type Tone = 'dark' | 'light'

const THEMES: Record<string, { card: string; tone: Tone }> = {
  black: { card: 'bg-civo-black', tone: 'dark' },
  cloud: { card: 'bg-civo-cloud', tone: 'light' },
  lime: { card: 'bg-civo-lime', tone: 'light' },
}

const OVERLAYS: Record<string, string> = {
  subtle: styles.overlaySubtle,
  medium: styles.overlayMedium,
  strong: styles.overlayStrong,
}

function isHubspotform(value: NewsletterFormBlockProps['hubspotform']): value is Hubspotform {
  return typeof value === 'object' && value !== null && 'portalId' in value
}

const HubSpotForm: React.FC<{
  hubspotform: NewsletterFormBlockProps['hubspotform']
  tone: Tone
  className?: string
}> = ({ hubspotform, tone, className }) => {
  const form = isHubspotform(hubspotform) ? hubspotform : null

  // Hook is called unconditionally - it handles the empty-config case itself
  const { containerRef, isLoading, error } = useHubSpotForm({
    portalId: form?.portalId || '',
    formId: form?.formId || '',
    formName: form?.title || '',
    region: form?.region,
  })

  const toneText = tone === 'dark' ? 'text-white' : 'text-civo-black'

  if (!form?.portalId || !form?.formId) {
    return (
      <div className={formStyles.errorContainer}>
        Form configuration unavailable. Please try again later.
      </div>
    )
  }

  if (error) {
    return <div className={formStyles.errorContainer}>{error}</div>
  }

  return (
    <div className={cn(formStyles.form, formStyles.newsletter, className)}>
      {isLoading && (
        <div className={cn(formStyles.loadingContainer, toneText)} aria-live="polite">
          <span className={formStyles.loadingSpinner} aria-hidden="true" />
          Loading form...
        </div>
      )}
      <div ref={containerRef} />
    </div>
  )
}

export const NewsletterFormBlock: React.FC<Props> = ({
  layout = 'split',
  variation = 'onCard',
  eyebrow,
  title,
  description,
  contentAlignment = 'middle',
  hubspotform,
  theme = 'black',
  backgroundImage,
  overlayStrength = 'medium',
  paddingTop,
  paddingBottom,
  disableInnerContainer: _disableInnerContainer,
}) => {
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  const useImage = theme === 'image'
  const image =
    useImage && typeof backgroundImage === 'object' ? (backgroundImage as Media) : undefined
  const imageUrl = image ? getMediaUrl(image.url, image.updatedAt) : undefined

  // Black is also the fallback when the image theme is picked before an image is uploaded
  const { card, tone } = useImage
    ? { card: 'bg-civo-black', tone: 'dark' as Tone }
    : (THEMES[theme ?? 'black'] ?? THEMES.black)

  const inPanel = variation === 'inPanel'
  const isSplit = (layout ?? 'split') === 'split'
  const isNarrow = layout === 'centeredNarrow'

  // Only bites once the columns sit side by side and the form is the taller one
  const copyAlignClass = {
    top: 'lg:self-start',
    middle: 'lg:self-center',
    bottom: 'lg:self-end',
  }[contentAlignment ?? 'middle']

  const eyebrowClass = tone === 'dark' ? 'text-civo-lime' : 'text-civo-black'
  const titleClass = tone === 'dark' ? 'text-white' : 'text-civo-black'
  const descriptionClass = tone === 'dark' ? 'text-white/70' : 'text-black/70'

  const invertSubmit = theme === 'lime'

  // A panel is always a light surface, whatever the card tone
  const formThemeClass = cn(
    inPanel ? formStyles.panelFields : tone === 'dark' ? formStyles.themeDark : formStyles.themeLight,
    invertSubmit && formStyles.submitInverted,
    !isSplit && formStyles.centeredConsent,
    isNarrow && formStyles.stacked,
  )

  const contentClass = isSplit
    ? 'lg:flex-row lg:items-center lg:gap-16'
    : isNarrow
      ? 'lg:gap-10'
      : 'lg:gap-12'

  // 46% matches the design's own 520/1120 copy-to-form ratio; pinned to 520px once xl has room
  const copyClass = isSplit
    ? cn('lg:w-[46%] lg:shrink-0 lg:gap-5 xl:w-[520px]', copyAlignClass)
    : cn('mx-auto w-full text-center', isNarrow ? 'max-w-[640px]' : 'max-w-[720px]')

  const formColumnClass = isSplit
    ? 'lg:min-w-0 lg:flex-1'
    : cn('mx-auto w-full', isNarrow ? 'max-w-[480px]' : 'max-w-[720px]')

  const formTone: Tone = inPanel ? 'light' : tone
  const panelClass = tone === 'dark' ? 'bg-civo-cloud' : 'bg-white'

  return (
    <div className={paddingClasses}>
      <div
        className={cn(
          'relative overflow-hidden rounded-[16px]',
          card,
          isNarrow && 'lg:mx-auto lg:max-w-[880px]',
        )}
      >
        {imageUrl && (
          <>
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${imageUrl})` }}
            />
            <div
              aria-hidden="true"
              className={cn(
                'absolute inset-0',
                isSplit && styles.split,
                OVERLAYS[overlayStrength ?? 'medium'] ?? OVERLAYS.medium,
              )}
            />
          </>
        )}

        <div className={cn('relative flex flex-col gap-8 p-8 md:p-12', contentClass)}>
          <div className={cn('flex flex-col gap-4', copyClass)}>
            <div className="flex flex-col gap-2 lg:gap-4">
              {eyebrow && <p className={cn('subheading-xsmall mb-0', eyebrowClass)}>{eyebrow}</p>}
              <h2 className={cn('display-medium mb-0', titleClass)}>{title}</h2>
            </div>
            {description && (
              <p className={cn('body-large mb-0', descriptionClass)}>{description}</p>
            )}
          </div>

          <div className={formColumnClass}>
            {inPanel ? (
              <div className={cn('rounded-[16px] p-4 lg:p-6', panelClass)}>
                <HubSpotForm
                  hubspotform={hubspotform}
                  tone={formTone}
                  className={formThemeClass}
                />
              </div>
            ) : (
              <HubSpotForm hubspotform={hubspotform} tone={formTone} className={formThemeClass} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
